import { NodeApi } from 'platejs';
import { isEmpty } from 'remeda';
import z from 'zod';

import { CropMarks, Point } from '@@api/utils/schemas/schemas';
import { createElement } from '@@plate/serialization/creators/createElement';

import { Caption, type CaptionElement } from './Caption';
import { Credit, type CreditElement } from './Credit';
import { ParagraphCodec, UnityParagraph } from './Paragraph';
import { TextElementsCodec } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, MIME_TYPES, UNITY_ELEMENT_TYPES } from '../constants';

const BaseUnityVideoCms = z.object({
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    contentAttributes: z.object({
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullable(),
        credit: z.string().nullish(),
        previewImage: z
            .object({
                url: z.string().nullable(),
                elvisId: z.string().nullable(),
                focusPoint: Point.nullish(),
                cropMarks: CropMarks.nullish(),
                caption: z
                    .object({
                        richTextFormat: z.array(UnityParagraph),
                    })
                    .nullish(),
                mimetype: z.nativeEnum(MIME_TYPES).nullish(),
            })
            .nullish(),
    }),
    originalSrc: z.string(),
    src: z.string(),
});

const UnityVideoCmsLivestream = z.object({
    ...BaseUnityVideoCms.shape,
    type: z.literal(UNITY_ELEMENT_TYPES.VIDEO_CMS_LIVESTREAM),
    mimetype: z.literal(MIME_TYPES.VIDEO_CMS_LIVESTREAM),
});

const UnityVideoCmsNormal = z.object({
    ...BaseUnityVideoCms.shape,
    type: z.literal(UNITY_ELEMENT_TYPES.VIDEO_CMS),
    mimetype: z.literal(MIME_TYPES.VIDEO_CMS),
});

export const UnityVideoCms = z.discriminatedUnion('type', [
    UnityVideoCmsNormal,
    UnityVideoCmsLivestream,
]);

export type UnityVideoCms = z.infer<typeof UnityVideoCms>;

export const PlateVideoCms = z.object({
    type: z.literal(ELEMENT_TYPES.VIDEO_CMS),
    id: ElementId,
    data: z.object({
        mimetype: z.union([
            z.literal(MIME_TYPES.VIDEO_CMS),
            z.literal(MIME_TYPES.VIDEO_CMS_LIVESTREAM),
        ]),
        templateElement: z.boolean().optional(),
        originalSrc: z.string(),
        src: z.string(),
        previewImage: z
            .object({
                url: z.string().nullable(),
                elvisId: z.string().nullable(),
                focusPoint: Point.nullish(),
                cropMarks: CropMarks.nullish(),
                caption: z.any().nullish(),
            })
            .nullish(),
    }),
    children: z.tuple([Caption, Credit]),
});
export type VideoCmsElement = z.infer<typeof PlateVideoCms>;

export const VideoCmsCodec = z.codec(UnityVideoCms, PlateVideoCms, {
    decode: (value) => {
        const captionItems = value.contentAttributes.caption?.richTextFormat[0]?.items;
        const captionData: CaptionElement['data'] = {
            ...(value.templateElement ? { templateElement: value.templateElement } : {}),
        };
        const caption: CaptionElement = {
            type: ELEMENT_TYPES.CAPTION,
            id:
                value.contentAttributes.caption?.richTextFormat[0]?.elementId ??
                crypto.randomUUID(),
            ...(isEmpty(captionData) ? {} : { data: captionData }),
            children: captionItems ? TextElementsCodec.decode(captionItems) : [{ text: '' }],
        };

        const credit = createElement(ELEMENT_TYPES.CREDIT, {
            children: [{ text: value.contentAttributes.credit ?? '' }],
        });

        const decodedVideoCms = {
            type: ELEMENT_TYPES.VIDEO_CMS,
            id: value.elementId ?? crypto.randomUUID(),
            data: {
                mimetype: value.mimetype,
                originalSrc: value.originalSrc,
                src: value.src,
                previewImage: value.contentAttributes.previewImage,
                ...(value.templateElement ? { templateElement: value.templateElement } : {}),
            },
            children: [caption, credit] satisfies [CaptionElement, CreditElement],
        };

        if (value.mimetype === MIME_TYPES.VIDEO_CMS) {
            return {
                ...decodedVideoCms,
                data: {
                    ...decodedVideoCms.data,
                    mimetype: MIME_TYPES.VIDEO_CMS,
                },
            };
        }
        return {
            ...decodedVideoCms,
            data: {
                ...decodedVideoCms.data,
                mimetype: MIME_TYPES.VIDEO_CMS_LIVESTREAM,
            },
        };
    },
    encode: (value) => {
        const captionElement = ParagraphCodec.encode({
            ...value.children[0],
            type: ELEMENT_TYPES.PARAGRAPH,
        });

        const creditElement = NodeApi.string(value.children[1]);

        const base = {
            elementId: value.id,
            version: CURRENT_VERSION,
            contentAttributes: {
                caption: {
                    richTextFormat: [captionElement],
                },
                credit: creditElement,
                previewImage: value.data.previewImage,
            },
            originalSrc: value.data.originalSrc,
            src: value.data.src,
            ...(value.data?.templateElement ? { templateElement: value.data.templateElement } : {}),
        };

        if (value.data.mimetype === MIME_TYPES.VIDEO_CMS) {
            return {
                ...base,
                type: UNITY_ELEMENT_TYPES.VIDEO_CMS,
                mimetype: MIME_TYPES.VIDEO_CMS,
            };
        }

        return {
            ...base,
            type: UNITY_ELEMENT_TYPES.VIDEO_CMS_LIVESTREAM,
            mimetype: value.data.mimetype,
        };
    },
});
